import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./logoPreviewRound2.css";

const LOGO_SRC = "/img/logos/logo-candidate.png";
const WORDMARK_COLOR = "#2563eb";

const FONT_OPTIONS = [
  {
    id: "inter",
    name: "Inter",
    fontFamily: '"Inter", system-ui, sans-serif',
    note: "Neutral and product-friendly",
  },
  {
    id: "instrument-sans",
    name: "Instrument Sans",
    fontFamily: '"Instrument Sans", system-ui, sans-serif',
    note: "Clean lowercase shapes",
  },
  {
    id: "manrope",
    name: "Manrope",
    fontFamily: '"Manrope", system-ui, sans-serif',
    note: "Soft geometric feel",
  },
  {
    id: "outfit",
    name: "Outfit",
    fontFamily: '"Outfit", system-ui, sans-serif',
    note: "Modern and bold",
  },
  {
    id: "plus-jakarta-sans",
    name: "Plus Jakarta Sans",
    fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
    note: "Balanced and approachable",
  },
  {
    id: "sora",
    name: "Sora",
    fontFamily: '"Sora", system-ui, sans-serif',
    note: "Sharper tech brand tone",
  },
  {
    id: "urbanist",
    name: "Urbanist",
    fontFamily: '"Urbanist", system-ui, sans-serif',
    note: "Rounder, lighter look",
  },
  {
    id: "poppins",
    name: "Poppins",
    fontFamily: '"Poppins", system-ui, sans-serif',
    note: "Friendly geometric shapes",
  },
  {
    id: "dm-sans",
    name: "DM Sans",
    fontFamily: '"DM Sans", system-ui, sans-serif',
    note: "Compact and polished",
  },
  {
    id: "lexend",
    name: "Lexend",
    fontFamily: '"Lexend", system-ui, sans-serif',
    note: "Open counters, easy to read",
  },
  {
    id: "space-grotesk",
    name: "Space Grotesk",
    fontFamily: '"Space Grotesk", system-ui, sans-serif',
    note: "Distinctive modern tech feel",
  },
  {
    id: "figtree",
    name: "Figtree",
    fontFamily: '"Figtree", system-ui, sans-serif',
    note: "Simple and contemporary",
  },
  {
    id: "archivo",
    name: "Archivo",
    fontFamily: '"Archivo", system-ui, sans-serif',
    note: "Stronger brand presence",
  },
];

const NAME_SUGGESTIONS = [
  "amptrans",
  "ampflow",
  "ampfleet",
  "voltlane",
  "gridpath",
  "lumatra",
];

function normalizeBrandName(value) {
  return value.toLowerCase().replace(/[^a-z\s-]/g, "").replace(/\s+/g, " ").trimStart();
}

function LogoPreviewRound2() {
  const [brandName, setBrandName] = useState("amptrans");
  const [selectedFontId, setSelectedFontId] = useState(FONT_OPTIONS[0].id);

  const selectedFont = useMemo(
    () => FONT_OPTIONS.find((option) => option.id === selectedFontId) ?? FONT_OPTIONS[0],
    [selectedFontId]
  );

  const previewName = brandName.trim() || "amptrans";

  return (
    <div className="min-h-screen bg-slate-100 font-ui">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Logo Preview Round 2</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-600 sm:text-base">
              Focus on one logo candidate and compare lowercase wordmark pairings side by side.
            </p>
          </div>
          <Link
            to="/logo-preview"
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            Back to Round 1
          </Link>
        </div>

        <section className="mb-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]">
            <div>
              <label htmlFor="brand-name" className="mb-2 block text-sm font-semibold text-slate-800">
                Lowercase company name
              </label>
              <input
                id="brand-name"
                type="text"
                value={brandName}
                onChange={(event) => setBrandName(normalizeBrandName(event.target.value))}
                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-lg text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                placeholder="type a lowercase name"
                spellCheck="false"
              />
              <p className="mt-2 text-xs text-slate-500">
                Input is automatically normalized to lowercase letters, spaces, and hyphens.
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                {NAME_SUGGESTIONS.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => setBrandName(suggestion)}
                    className={`rounded-full border px-3 py-1.5 text-sm transition ${
                      previewName === suggestion
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-white"
                    }`}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-semibold text-slate-800">Layout comparison</p>
              <p className="mt-1 text-sm text-slate-500">
                {selectedFont.name} · {selectedFont.note}
              </p>
              <div className="mt-4 grid gap-4">
                <div className="rounded-2xl border border-slate-200 bg-white p-6">
                  <p className="mb-4 text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                    Wordmark on the right
                  </p>
                  <div className="flex flex-wrap items-center gap-4 sm:gap-5">
                    <img
                      src={LOGO_SRC}
                      alt="Logo candidate"
                      className="h-16 w-auto object-contain sm:h-20"
                    />
                    <div
                      className="logo-preview-round-2-wordmark text-3xl sm:text-4xl"
                      style={{ color: WORDMARK_COLOR, fontFamily: selectedFont.fontFamily }}
                    >
                      {previewName}
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-6">
                  <p className="mb-4 text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                    Wordmark below
                  </p>
                  <div className="flex flex-col items-start gap-4">
                    <img
                      src={LOGO_SRC}
                      alt="Logo candidate stacked"
                      className="h-16 w-auto object-contain sm:h-20"
                    />
                    <div
                      className="logo-preview-round-2-wordmark text-3xl sm:text-4xl"
                      style={{ color: WORDMARK_COLOR, fontFamily: selectedFont.fontFamily }}
                    >
                      {previewName}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-8">
          <div className="mb-4 flex items-end justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Font comparison</h2>
              <p className="mt-1 text-sm text-slate-600">
                Click a card to promote that font into the main preview above.
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {FONT_OPTIONS.map((option) => {
              const isSelected = option.id === selectedFontId;

              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setSelectedFontId(option.id)}
                  className={`rounded-2xl border p-5 text-left shadow-sm transition ${
                    isSelected
                      ? "border-blue-500 bg-blue-50 ring-2 ring-blue-100"
                      : "border-slate-200 bg-white hover:border-slate-300 hover:shadow"
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{option.name}</p>
                      <p className="mt-1 text-xs text-slate-500">{option.note}</p>
                    </div>
                    {isSelected && (
                      <span className="rounded-full bg-blue-600 px-2.5 py-1 text-xs font-medium text-white">
                        Selected
                      </span>
                    )}
                  </div>

                  <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5">
                    <div className="grid gap-4">
                      <div>
                        <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
                          Right
                        </p>
                        <div className="flex items-center gap-4">
                          <img
                            src={LOGO_SRC}
                            alt=""
                            className="h-12 w-auto object-contain"
                          />
                          <div
                            className="logo-preview-round-2-wordmark text-3xl"
                            style={{ color: WORDMARK_COLOR, fontFamily: option.fontFamily }}
                          >
                            {previewName}
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
                          Below
                        </p>
                        <div className="flex flex-col items-start gap-3">
                          <img
                            src={LOGO_SRC}
                            alt=""
                            className="h-12 w-auto object-contain"
                          />
                          <div
                            className="logo-preview-round-2-wordmark text-3xl"
                            style={{ color: WORDMARK_COLOR, fontFamily: option.fontFamily }}
                          >
                            {previewName}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Quick usage checks</h2>
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">Navbar scale</p>
              <div className="mt-4 flex items-center justify-between gap-4 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                <div className="flex items-center gap-3">
                  <img src={LOGO_SRC} alt="" className="h-9 w-auto object-contain" />
                  <div
                    className="logo-preview-round-2-wordmark text-[1.65rem] leading-none"
                    style={{ color: WORDMARK_COLOR, fontFamily: selectedFont.fontFamily }}
                  >
                    {previewName}
                  </div>
                </div>
                <div className="text-sm text-slate-400">about · login</div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">Stacked scale</p>
              <div className="mt-4 flex flex-col items-start gap-3 rounded-xl border border-slate-100 bg-slate-50 px-4 py-4">
                <img src={LOGO_SRC} alt="" className="h-8 w-auto object-contain" />
                <div
                  className="logo-preview-round-2-wordmark text-[1.35rem] leading-none"
                  style={{ color: WORDMARK_COLOR, fontFamily: selectedFont.fontFamily }}
                >
                  {previewName}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default LogoPreviewRound2;
